import React, { FC, ReactElement, ReactNode, useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.less';

export type TabsType = 'line' | 'card' | 'card-gutter' | 'text' | 'rounded' | 'capsule' | string;
export type TabsSize = 'mini' | 'small' | 'default' | 'large';

export interface TabsProps {
  style?: React.CSSProperties;
  className?: string | string[];
  children?: ReactNode;
  defaultActiveTab?: string;
  activeTab?: string;
  size?: TabsSize;
  type?: TabsType;
  destroyOnHide?: boolean;
  onChange?: (key: string) => void;
  onClickTab?: (key: string) => void;
}

export interface TabPaneProps {
  title: ReactNode;
  children?: ReactNode;
  className?: string | string[];
  style?: React.CSSProperties;
  destroyOnHide?: boolean;
  disabled?: boolean;
}

type PaneInfo = {
  key: string;
  props: TabPaneProps;
};

const toClassName = (className?: string | string[]) => {
  return Array.isArray(className) ? className.join(' ') : className;
};

const normalizePaneKey = (key: React.Key | null, fallback: number) => {
  if (key === null) {
    return String(fallback);
  }

  return String(key).replace(/^\.\$/, '').replace(/^\./, '');
};

const getPanes = (children: ReactNode): PaneInfo[] => {
  return React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child, index) => {
      const element = child as ReactElement<TabPaneProps>;

      return {
        key: normalizePaneKey(element.key, index),
        props: element.props,
      };
    });
};

export const TabsTabPane: FC<TabPaneProps> = ({ children }) => {
  return <>{children}</>;
};

export const TabsBase: FC<TabsProps> = (props) => {
  const {
    activeTab,
    className,
    defaultActiveTab,
    destroyOnHide,
    children,
    onChange,
    onClickTab,
    size = 'default',
    style,
    type = 'line',
  } = props;
  const panes = useMemo(() => getPanes(children), [children]);
  const firstKey = panes[0]?.key;
  const [innerActiveTab, setInnerActiveTab] = useState(defaultActiveTab ?? firstKey);
  const currentActiveTab = activeTab ?? innerActiveTab ?? firstKey;
  const activePane = panes.find((pane) => pane.key === currentActiveTab) ?? panes[0];
  const rootClassName = classNames(
    'tabs-ui',
    `tabs-ui-${type}`,
    `tabs-ui-size-${size}`,
    toClassName(className)
  );

  const handleTabClick = (pane: PaneInfo) => {
    if (pane.props.disabled) {
      return;
    }

    onClickTab?.(pane.key);

    if (pane.key !== currentActiveTab) {
      if (activeTab === undefined) {
        setInnerActiveTab(pane.key);
      }

      onChange?.(pane.key);
    }
  };

  return (
    <div className={rootClassName} style={style}>
      <div className="tabs-ui-header-wrapper">
        <div className="tabs-ui-header" role="tablist">
          {panes.map((pane) => {
            const active = pane.key === currentActiveTab;

            return (
              <button
                aria-selected={active}
                className={classNames('tabs-ui-header-title', {
                  'tabs-ui-header-title-active': active,
                  'tabs-ui-header-title-disabled': pane.props.disabled,
                })}
                disabled={pane.props.disabled}
                key={pane.key}
                onClick={() => handleTabClick(pane)}
                role="tab"
                type="button"
              >
                <span className="tabs-ui-header-title-text">{pane.props.title}</span>
              </button>
            );
          })}
        </div>
        <div className="tabs-ui-header-ink" />
      </div>
      <div className="tabs-ui-content">
        {destroyOnHide || activePane?.props.destroyOnHide ? (
          activePane?.props.children
        ) : (
          panes.map((pane) => (
            <div
              className={classNames('tabs-ui-pane', {
                'tabs-ui-pane-active': pane.key === currentActiveTab,
              })}
              hidden={pane.key !== currentActiveTab}
              key={pane.key}
            >
              {pane.props.children}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

type TabsUIProps = TabsProps & {
  active?: boolean;
  data?: { name: ReactNode }[];
  childrenTabs?: { name: ReactNode }[];
  activeTab?: string;
  activeTab2?: string;
  onChangeTab?: (key: string) => void;
  onChangeTab2?: (key: string) => void;
};

const TabsUI: FC<TabsUIProps> = (props) => {
  const { data = [], childrenTabs, activeTab, onChangeTab, activeTab2, onChangeTab2, onClickTab, type } = props;

  const renderChildrenTabs = () => {
    return (
      <div>
        <TabsBase
          className={styles['tabsContainer-tab']}
          activeTab={activeTab2}
          onChange={onChangeTab2}
          size="large"
          style={{ width: '100%' }}
        >
          {childrenTabs?.map((item, index) => (
            <TabsTabPane key={index} title={item.name}>
              {props.children}
            </TabsTabPane>
          ))}
        </TabsBase>
      </div>
    );
  };

  return (
    <div>
      <TabsBase
        className={styles.tabsContainer}
        activeTab={activeTab}
        onChange={onChangeTab}
        onClickTab={onClickTab}
        size="large"
        type={type}
      >
        {data.map((item, index) => (
          <TabsTabPane key={index} title={item.name}>
            {childrenTabs ? renderChildrenTabs() : props.children}
          </TabsTabPane>
        ))}
      </TabsBase>
    </div>
  );
};

export default TabsUI;
